import { PlainDate } from "@sa-nu/shared-utils/date";
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useResponsiveValue } from "../../../../_hooks/use-responsive-value";
import type { ReservationCalendarNavigationSize } from "../reservation-calendar";

const DEBOUNCE_MS = 500;

/**
 * カレンダーの日付範囲を一元管理するフック
 *
 * 即時日付（firstNight/lastNight）と debounced 日付（debouncedFirstNight/debouncedLastNight）の
 * 2層構造で管理する。UIの日付表示は即時更新しつつ、GraphQLクエリ用の日付は
 * 最後の操作から500ms経過後に更新する。
 *
 * jumpTo を使うと debounce をバイパスして即時に debounced 値を更新できる。
 * モーダル確定時など連打でない離散的操作で、cico と availability の同期を保つために使用する。
 *
 * onSettleRef に関数をセットすると、debounce settle 時に移動日数(delta)を受け取れる。
 * startTransition 内で同期的に呼ばれるため、settle と同じ transition でバッチされる。
 * jumpTo 経由の場合は timer を通らないため呼ばれない。
 */
export function useReservationCalendarDateRange({
  minNight,
  maxNight,
  initialDate,
}: {
  minNight: PlainDate;
  maxNight: PlainDate;
  initialDate?: PlainDate;
}) {
  const navigationSize: ReservationCalendarNavigationSize = useResponsiveValue({
    base: 7,
    md: 14,
  });

  const [firstNight, setFirstNightInternal] = useState(
    () => initialDate ?? minNight,
  );

  const lastNight = useMemo(
    () => firstNight.add({ days: navigationSize - 1 }),
    [firstNight, navigationSize],
  );

  const setFirstNight = useCallback(
    (
      selectedFirstNightOrUpdater: PlainDate | ((prev: PlainDate) => PlainDate),
    ) => {
      setFirstNightInternal((prev) => {
        const selectedFirstNight =
          typeof selectedFirstNightOrUpdater === "function"
            ? selectedFirstNightOrUpdater(prev)
            : selectedFirstNightOrUpdater;
        return getNavigatableFirstNight(
          minNight,
          maxNight,
          selectedFirstNight,
          navigationSize,
        );
      });
    },
    [minNight, maxNight, navigationSize],
  );

  const [debouncedFirstNight, setDebouncedFirstNight] = useState(firstNight);

  const debouncedLastNight = useMemo(
    () => debouncedFirstNight.add({ days: navigationSize - 1 }),
    [debouncedFirstNight, navigationSize],
  );

  /** debounce をバイパスして firstNight と debouncedFirstNight を同時に更新する。境界クランプ付き。 */
  const jumpTo = useCallback(
    (first: PlainDate) => {
      const clamped = getNavigatableFirstNight(
        minNight,
        maxNight,
        first,
        navigationSize,
      );
      setFirstNightInternal(clamped);
      setDebouncedFirstNight(clamped);
    },
    [minNight, maxNight, navigationSize],
  );

  // debounce settle 時のコールバック。外部から ref 経由でセットする。
  // startTransition 内で同期的に呼ばれるため、debouncedFirstNight の更新と同じ transition でバッチされる。
  const onSettleRef = useRef<((deltaDays: number) => void) | undefined>(
    undefined,
  );

  // debouncedFirstNight を deps から除外する理由:
  // deps に含めると確定値の更新のたびに effect が再実行され、
  // debounce の意味（連打の最終到達点まで待つ）が壊れるため。
  useEffect(
    () => {
      const timer = setTimeout(() => {
        startTransition(() => {
          const delta = debouncedFirstNight
            .until(firstNight)
            .total({ unit: "day" });
          if (delta === 0) return;
          setDebouncedFirstNight(firstNight);
          onSettleRef.current?.(delta);
        });
      }, DEBOUNCE_MS);
      return () => clearTimeout(timer);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debouncedFirstNight は上記コメントのとおり除外
    [firstNight],
  );

  const unsettledFirstNightDelta = useMemo(
    () => debouncedFirstNight.until(firstNight).total({ unit: "day" }),
    [debouncedFirstNight, firstNight],
  );

  return {
    firstNight,
    lastNight,
    setFirstNight,
    jumpTo,
    navigationSize,
    debouncedFirstNight,
    debouncedLastNight,
    unsettledFirstNightDelta,
    onSettleRef,
  };
}

/**
 * 境界クランプ関数
 *
 * 選択された日付を起点として、表示可能な日付範囲を計算する。
 * useCICO が jumpTo に渡す値を事前にクランプするためにも使用する。
 */
export function getNavigatableFirstNight(
  minNight: PlainDate,
  maxNight: PlainDate,
  selectedFirstNight: PlainDate,
  navigationSize: number,
): PlainDate {
  const calculatedLastNight = selectedFirstNight.add({
    days: navigationSize - 1,
  });

  if (PlainDate.compare(selectedFirstNight, minNight) < 0) {
    return minNight;
  }
  if (PlainDate.compare(calculatedLastNight, maxNight) > 0) {
    return maxNight.add({ days: -(navigationSize - 1) });
  }
  return selectedFirstNight;
}
